import { type FormEventHandler, type ReactNode, useEffect } from 'react'
import { X } from 'lucide-react'
import { IconButton } from '../IconButton'

let openModalCount = 0
let previousBodyOverflow = ''
let previousHtmlOverflow = ''

type ModalProps = {
  ariaLabelledBy?: string
  bodyClassName?: string
  backdropClassName?: string
  children: ReactNode
  className?: string
  closeLabel?: string
  isDismissDisabled?: boolean
  mobilePresentation?: 'modal' | 'sheet'
  onClose: () => void
  onSubmit?: FormEventHandler<HTMLFormElement>
  panelClassName?: string
  title?: ReactNode
}

export const Modal = ({
  ariaLabelledBy,
  bodyClassName,
  backdropClassName,
  children,
  className,
  closeLabel = 'Close',
  isDismissDisabled = false,
  mobilePresentation = 'modal',
  onClose,
  onSubmit,
  panelClassName,
  title
}: ModalProps) => {
  useEffect(() => {
    if (openModalCount === 0) {
      previousBodyOverflow = document.body.style.overflow
      previousHtmlOverflow = document.documentElement.style.overflow
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    }

    openModalCount += 1

    return () => {
      openModalCount = Math.max(0, openModalCount - 1)

      if (openModalCount === 0) {
        document.body.style.overflow = previousBodyOverflow
        document.documentElement.style.overflow = previousHtmlOverflow
      }
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isDismissDisabled) {
        onClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isDismissDisabled, onClose])

  const panelClasses = [
    'ui-modal-panel',
    'modal-panel',
    mobilePresentation === 'sheet' ? 'ui-modal-panel--mobile-sheet' : '',
    bodyClassName ? 'ui-modal-panel--with-body' : '',
    panelClassName,
    className
  ].filter(Boolean).join(' ')

  const content = (
    <>
      {title !== undefined && (
        <div className="ui-modal-heading modal-heading">
          {title}
          <IconButton
            disabled={isDismissDisabled}
            icon={<X size={18} />}
            label={closeLabel}
            onClick={onClose}
            className="modal-close-button"
          />
        </div>
      )}
      {bodyClassName ? (
        <div className={['ui-modal-body', 'modal-body', bodyClassName].filter(Boolean).join(' ')}>
          {children}
        </div>
      ) : children}
    </>
  )

  return (
    <div
      className={[
        'ui-modal-backdrop',
        'modal-backdrop',
        mobilePresentation === 'sheet' ? 'ui-modal-backdrop--mobile-sheet' : '',
        backdropClassName
      ].filter(Boolean).join(' ')}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isDismissDisabled) {
          onClose()
        }
      }}
    >
      {onSubmit ? (
        <form
          aria-labelledby={ariaLabelledBy}
          aria-modal="true"
          className={panelClasses}
          onSubmit={onSubmit}
          role="dialog"
        >
          {content}
        </form>
      ) : (
        <section
          aria-labelledby={ariaLabelledBy}
          aria-modal="true"
          className={panelClasses}
          onClick={(event) => event.stopPropagation()}
          role="dialog"
        >
          {content}
        </section>
      )}
    </div>
  )
}
